import React, { useState, useEffect } from 'react';
import { AdAsset } from '../types';
import { ExternalLink } from 'lucide-react';
import DOMPurify from 'dompurify';

interface AdBannerProps {
    assets: AdAsset[];
    variant: 'tv' | 'mobile';
    theme?: 'dark' | 'light';
    primaryColor?: string;
    intervalOverride?: number;
    onCycleComplete?: () => void;
    hospitalId?: string; // For click tracking
}

const AdBanner: React.FC<AdBannerProps> = ({ assets, variant, theme = 'light', primaryColor = '#5B65DC', intervalOverride, onCycleComplete, hospitalId }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const filteredAssets = assets
        .filter(a => a.isActive && (a.type === 'BANNER_TEXT' || a.type === 'BANNER_IMAGE'))
        .filter(a => {
            if (a.target === 'ALL') return true;
            if (a.target === 'TV_ONLY' && variant === 'tv') return true;
            if (a.target === 'MOBILE_ONLY' && variant === 'mobile') return true;
            return false;
        })
        .sort((a, b) => a.priority - b.priority);

    useEffect(() => {
        if (filteredAssets.length === 0) return;

        const current = filteredAssets[currentIndex];
        const interval = intervalOverride || (current?.displayTimeSec || 15);

        // Case: Only 1 asset
        if (filteredAssets.length === 1) {
            const timer = setTimeout(() => {
                onCycleComplete?.();
            }, interval * 1000);
            return () => clearTimeout(timer);
        }

        // Case: Multiple assets
        const timer = setTimeout(() => {
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentIndex((prev) => {
                    const next = prev + 1;
                    if (next === filteredAssets.length) {
                        onCycleComplete?.();
                        return 0;
                    }
                    return next;
                });
                setIsTransitioning(false);
            }, 400);
        }, interval * 1000);

        return () => clearTimeout(timer);
    }, [filteredAssets.length, currentIndex, intervalOverride]);

    if (filteredAssets.length === 0) return null;

    const current = filteredAssets[currentIndex % filteredAssets.length];
    const isDark = theme === 'dark';
    const isMobile = variant === 'mobile';
    const hasLink = isMobile && current.linkUrl;

    // Fire-and-forget click tracking
    const trackClick = () => {
        if (!hospitalId || !current.id) return;
        const sessionHash = btoa(navigator.userAgent).slice(0, 12);
        fetch('/api/ads/track', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                hospitalId,
                adId: current.id,
                adType: current.type,
                source: 'mobile',
                eventType: 'click',
                sessionHash,
            }),
        }).catch(() => {}); // Fire-and-forget
    };

    const determineTextSize = () => {
        const size = current.textSize || 'md';
        if (size === 'xl') return isMobile ? 'text-2xl pt-1' : 'text-3xl';
        if (size === 'lg') return isMobile ? 'text-xl pt-0.5' : 'text-2xl';
        if (size === 'sm') return isMobile ? 'text-sm' : 'text-lg';
        return isMobile ? 'text-base' : 'text-xl';
    };

    const handleClick = () => {
        if (!hasLink) return;
        trackClick();
        // Ensure URL has a protocol — browsers treat "www.google.com" as a relative path
        const raw = current.linkUrl!.trim();
        const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div
            className={`w-full overflow-hidden transition-all duration-500 ${isMobile
                ? 'rounded-2xl border shadow-sm'
                : 'rounded-xl border shadow-lg'
                } ${hasLink ? 'cursor-pointer active:scale-[0.98] active:shadow-md' : ''}`}
            style={{
                borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                background: isDark
                    ? 'linear-gradient(135deg, rgba(15,23,42,0.95), rgba(30,41,59,0.95))'
                    : 'linear-gradient(135deg, rgba(255,255,255,0.97), rgba(248,250,252,0.97))',
            }}
            onClick={handleClick}
        >
            <div
                className={`flex items-center w-full transition-all duration-400 ${isTransitioning ? 'opacity-0 translate-y-1' : 'opacity-100 translate-y-0'
                    } ${current.type === 'BANNER_IMAGE' ? 'p-0' : (isMobile ? 'gap-4 p-4' : 'gap-4 px-6 py-3')}`}
            >
                {current.type === 'BANNER_IMAGE' && current.imageUrl ? (
                    <div className="relative w-full">
                        <img
                            src={current.imageUrl}
                            alt={current.imageAlt || 'Promoción'}
                            className={`w-full object-cover ${isMobile ? 'h-auto max-h-[400px] rounded-2xl' : 'h-[100px] rounded-xl'}`}
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                            }}
                        />
                        {/* "Ver más" indicator for clickable mobile banners */}
                        {hasLink && (
                            <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-black/50 backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-lg">
                                Ver más <ExternalLink className="w-3 h-3" />
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        {current.icon && (
                            <div
                                className={`flex items-center justify-center flex-shrink-0 ${isMobile ? 'w-16 h-16 rounded-2xl border-2' : 'w-14 h-14 rounded-xl border-2'
                                    }`}
                                style={{
                                    backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                                }}
                            >
                                {current.icon.startsWith('http') ? (
                                    <img
                                        src={current.icon}
                                        alt=""
                                        className={isMobile ? 'w-10 h-10 object-contain' : 'w-8 h-8 object-contain'}
                                    />
                                ) : (
                                    <span className={isMobile ? 'text-3xl' : 'text-2xl'}>{current.icon}</span>
                                )}
                            </div>
                        )}
                        <div className="flex flex-col justify-center min-w-0 flex-1 relative">
                            <span
                                className={`font-black tracking-tight leading-tight truncate ${determineTextSize()}`}
                                style={{ color: isDark ? 'rgba(255,255,255,0.9)' : '#1e293b' }}
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(current.text || '') }}
                            />
                        </div>
                        {/* "Ver más" indicator for clickable mobile text banners */}
                        {hasLink && (
                            <div className="flex items-center gap-1 flex-shrink-0 text-indigo-500 opacity-70">
                                <ExternalLink className="w-4 h-4" />
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Shimmer effect */}
            <div
                className="absolute top-0 left-[-100%] w-1/2 h-full animate-[shimmer_8s_infinite]"
                style={{
                    background: `linear-gradient(90deg, transparent, ${isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.4)'
                        }, transparent)`,
                }}
            />
        </div>
    );
};

export default AdBanner;
